import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Chip, IconButton, Snackbar, Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import WhatshotRoundedIcon from '@mui/icons-material/WhatshotRounded';
import { gamesApi } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import { playMineCrash, playMineSafeClick, playMineWin, unlockMinesAudio } from './minesSounds';
import './MinesScreen.css';

const BET_OPTIONS = [100, 90, 80, 70, 60, 50, 30, 20, 15, 10, 8, 5, 3, 2, 1];
const ACTION_SAFETY_MS = 12000;

function formatMultiplier(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(2)));
}

function getPayoutTable(mineCount, multiplierTables) {
  return multiplierTables?.[String(mineCount)] ?? [];
}

function resolvePreviewGridSize(state, mineCount, session) {
  if (session?.gridSize) return session.gridSize;
  return state?.gridSize ?? 25;
}

/** Column count for a square N×N grid (default 5×5 = 25 tiles). */
function resolveGridColumns(gridSize) {
  const side = Math.round(Math.sqrt(gridSize));
  if (side * side === gridSize) return side;
  return Math.ceil(Math.sqrt(gridSize));
}

function MinesPickerPopup({ options, value, onSelect, prefix = '' }) {
  return (
    <Box className="mines-picker-popup" onClick={(e) => e.stopPropagation()}>
      <Box className="mines-picker-popup__grid">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`mines-picker-popup__option ${value === opt ? 'active' : ''}`}
            onClick={() => onSelect(opt)}
          >
            {prefix}{opt}
          </button>
        ))}
      </Box>
    </Box>
  );
}

function MinesMultiplierSlider({ multipliers, betAmount, activeIndex, nextIndex = -1 }) {
  const trackRef = useRef(null);
  const chipRefs = useRef([]);
  const userScrollingRef = useRef(false);
  const scrollIdleTimerRef = useRef(null);

  const scrollTrack = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * 120, behavior: 'smooth' });
  };

  const scrollTarget = activeIndex >= 0 ? activeIndex : nextIndex;

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const onScroll = () => {
      userScrollingRef.current = true;
      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
      scrollIdleTimerRef.current = window.setTimeout(() => {
        userScrollingRef.current = false;
      }, 900);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (scrollTarget < 0 || userScrollingRef.current) return;
    const chip = chipRefs.current[scrollTarget];
    const track = trackRef.current;
    if (!chip || !track) return;

    const chipLeft = chip.offsetLeft;
    const chipWidth = chip.offsetWidth;
    const trackWidth = track.clientWidth;
    const target = chipLeft - trackWidth / 2 + chipWidth / 2;
    track.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
  }, [scrollTarget, multipliers.length]);

  if (!multipliers.length) return null;

  return (
    <Box className="mines-mult-slider">
      <button
        type="button"
        className="mines-mult-slider__arrow"
        onClick={() => scrollTrack(-1)}
        aria-label="Scroll multipliers left"
      >
        <ChevronLeftRoundedIcon fontSize="small" />
      </button>

      <Box className="mines-mult-slider__track" ref={trackRef}>
        {multipliers.map((multiplier, index) => {
          const isActive = activeIndex === index;
          const isNext = nextIndex === index;
          const isPast = activeIndex >= 0 && index < activeIndex;

          return (
            <Box
              key={index}
              ref={(el) => { chipRefs.current[index] = el; }}
              className={[
                'mines-mult-slider__chip',
                isActive && 'mines-mult-slider__chip--active',
                isNext && 'mines-mult-slider__chip--next',
                isPast && 'mines-mult-slider__chip--past',
              ].filter(Boolean).join(' ')}
            >
              <span className="mines-mult-slider__mult">{formatMultiplier(multiplier)}x</span>
              {betAmount > 0 && (
                <span className="mines-mult-slider__win">
                  ₹{(betAmount * multiplier).toFixed(1)}
                </span>
              )}
            </Box>
          );
        })}
      </Box>

      <button
        type="button"
        className="mines-mult-slider__arrow"
        onClick={() => scrollTrack(1)}
        aria-label="Scroll multipliers right"
      >
        <ChevronRightRoundedIcon fontSize="small" />
      </button>
    </Box>
  );
}

function hideUnplayedActiveSession(data) {
  if (!data?.session || data.session.status !== 'ACTIVE') return data;
  if ((data.session.revealedTiles?.length ?? 0) > 0) return data;
  return { ...data, session: null };
}

function MinesScreen({ user, onBack, onWalletUpdate }) {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState('1');
  const [mineCount, setMineCount] = useState(3);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [betPopupOpen, setBetPopupOpen] = useState(false);
  const [minesPopupOpen, setMinesPopupOpen] = useState(false);
  const busyRef = useRef(false);
  const pickersRef = useRef(null);
  const defaultsAppliedRef = useRef(false);

  const session = state?.session;
  const isPlaying = session?.status === 'ACTIVE';
  const isEnded = session && session.status !== 'ACTIVE';
  const isIdle = !session;
  const stake = Number(amount) || 0;
  const activeMineCount = session?.mineCount ?? mineCount;
  const previewMineCount = isPlaying || isEnded ? activeMineCount : mineCount;
  const previewGridSize = resolvePreviewGridSize(state, previewMineCount, session);
  const gridCols = resolveGridColumns(previewGridSize);
  const payoutTable = getPayoutTable(previewMineCount, state?.multiplierTables);
  const activeBet = session?.amount ?? stake;
  const revealedCount = session?.revealedTiles?.filter(
    (tile) => !(session.minePositions?.includes(tile)),
  ).length ?? 0;
  const sliderActiveIndex = revealedCount > 0 ? revealedCount - 1 : -1;
  const sliderNextIndex = isPlaying && revealedCount < payoutTable.length ? revealedCount : -1;
  const currentWin = session?.potentialWin ?? 0;
  const minBet = state?.minBet ?? 1;
  const maxBet = state?.maxBet ?? 5000;
  const allowedMineCounts = state?.allowedMineCounts ?? [3, 5, 10, 15];
  const betOptions = BET_OPTIONS.filter((v) => v >= minBet && v <= maxBet);
  const canChangeSettings = !isPlaying;
  const canReload = !loading && !isPlaying && state?.enabled;

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  const load = useCallback((options = {}) => {
    gamesApi.minesState()
      .then((data) => {
        setState(options.hideUnplayedActive ? hideUnplayedActiveSession(data) : data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!state || defaultsAppliedRef.current) return;
    const revealedCount = state.session?.revealedTiles?.length ?? 0;
    const resumeActive = state.session?.status === 'ACTIVE' && revealedCount > 0;
    if (resumeActive) {
      setAmount(String(state.session.amount));
      setMineCount(state.session.mineCount);
      defaultsAppliedRef.current = true;
      return;
    }
    const minMines = Math.min(...(state.allowedMineCounts ?? [3]));
    setMineCount(minMines);
    setAmount(String(state.minBet ?? 1));
    defaultsAppliedRef.current = true;
  }, [state]);

  const runAction = useCallback(async (action) => {
    if (busyRef.current) return { ok: false, busy: true };
    busyRef.current = true;
    setLoading(true);
    const safetyTimer = window.setTimeout(() => {
      busyRef.current = false;
      setLoading(false);
    }, ACTION_SAFETY_MS);
    try {
      await action();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err };
    } finally {
      window.clearTimeout(safetyTimer);
      busyRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ hideUnplayedActive: true });
  }, [load]);

  useEffect(() => {
    unlockMinesAudio();
  }, []);

  useEffect(() => {
    if (!isEnded || !session) return;
    setAmount(String(session.amount));
    setMineCount(session.mineCount);
  }, [isEnded, session]);

  useEffect(() => {
    if (!betPopupOpen && !minesPopupOpen) return undefined;

    const closeIfOutside = (event) => {
      if (pickersRef.current?.contains(event.target)) return;
      setBetPopupOpen(false);
      setMinesPopupOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', closeIfOutside);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', closeIfOutside);
    };
  }, [betPopupOpen, minesPopupOpen]);

  const startGame = async () => {
    const stake = Number(amount);
    if (!Number.isFinite(stake) || stake < minBet) {
      setToast(`Minimum bet is ₹${minBet}`);
      return;
    }
    if (stake > maxBet) {
      setToast(`Maximum bet is ₹${maxBet}`);
      return;
    }
    if (stake > walletBalance) {
      setToast('Insufficient balance');
      return;
    }

    setBetPopupOpen(false);
    setMinesPopupOpen(false);
    const result = await runAction(async () => {
      const startResult = await gamesApi.minesStart({ amount: stake, mineCount });
      setState((prev) => ({
        ...(prev ?? {}),
        session: startResult.session,
      }));
      setToast('Game started — pick a tile!');
      await refreshUser();
    });
    if (result.busy) setToast('Please wait…');
    if (result.error) setToast(result.error.message);
  };

  const reloadGame = async () => {
    if (!canReload) return;
    setBetPopupOpen(false);
    setMinesPopupOpen(false);
    await startGame();
  };

  const revealTile = async (tileIndex) => {
    if (!session || !isPlaying || busyRef.current) return;
    if (session.revealedTiles?.includes(tileIndex)) return;

    const result = await runAction(async () => {
      const revealResult = await gamesApi.minesReveal({ sessionId: session.id, tileIndex });
      setState((prev) => ({ ...(prev ?? {}), session: revealResult.session }));
      if (revealResult.busted) {
        playMineCrash();
        setToast('Boom! You hit a mine');
      } else {
        playMineSafeClick();
        if (revealResult.autoCashOut) {
          setToast(`Perfect run! Won ₹${revealResult.payout}`);
          await refreshUser();
        } else {
          setToast(`Safe! Multiplier ${revealResult.session.currentMultiplier}x`);
        }
      }
    });
    if (result.busy) setToast('Please wait…');
    if (result.error) setToast(result.error.message);
  };

  const cashOut = async () => {
    if (!session || busyRef.current) return;

    const result = await runAction(async () => {
      const cashoutResult = await gamesApi.minesCashout(session.id);
      setState((prev) => ({ ...(prev ?? {}), session: cashoutResult.session }));
      playMineWin();
      setToast(`Cashed out ₹${cashoutResult.payout}!`);
      await refreshUser();
    });
    if (result.busy) setToast('Please wait…');
    if (result.error) setToast(result.error.message);
  };

  const getTileClass = (index) => {
    if (isIdle) return 'mines-tile mines-tile--hidden mines-tile--idle';
    const revealed = session.revealedTiles?.includes(index);
    const mines = session.minePositions ?? [];
    const bustedTile = session.bustedTile;
    const isMine = mines.includes(index);
    const isBustedClick = isEnded && bustedTile === index;

    if (isEnded && mines.length > 0) {
      if (isMine || isBustedClick) return 'mines-tile mines-tile--mine';
      if (revealed) return 'mines-tile mines-tile--gem';
      return 'mines-tile mines-tile--hidden-end';
    }
    if (revealed) return 'mines-tile mines-tile--gem';
    return 'mines-tile mines-tile--hidden';
  };

  return (
    <Box className="mines-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />

      <Box className="mines-header">
        <IconButton size="small" onClick={onBack} className="mines-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Box className="mines-header__center">
          <WhatshotRoundedIcon sx={{ fontSize: 22, color: '#fbbf24' }} />
          <Typography className="mines-title">Mines</Typography>
        </Box>
        <Chip
          size="small"
          label={`₹${walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`}
          className="mines-wallet-chip"
        />
      </Box>

      <Box className="mines-scroll">
        {payoutTable.length > 0 && (
          <MinesMultiplierSlider
            multipliers={payoutTable}
            betAmount={isPlaying || isEnded ? activeBet : stake}
            activeIndex={isPlaying || isEnded ? sliderActiveIndex : -1}
            nextIndex={isPlaying ? sliderNextIndex : -1}
          />
        )}

        <Box
          className="mines-grid"
          style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
        >
          {Array.from({ length: previewGridSize }).map((_, index) => (
            <button
              key={index}
              type="button"
              className={getTileClass(index)}
              onClick={() => revealTile(index)}
              disabled={loading || !isPlaying || session?.revealedTiles?.includes(index)}
            >
              {session?.revealedTiles?.includes(index)
                && !(isEnded && (session.minePositions?.includes(index) || session.bustedTile === index)) && (
                <DiamondRoundedIcon sx={{ fontSize: 22 }} />
              )}
              {isEnded && (session.minePositions?.includes(index) || session.bustedTile === index) && (
                <span className="mines-mine-icon">💣</span>
              )}
            </button>
          ))}
        </Box>

        <Box className="mines-balance-bar">
          <Typography className="mines-balance-bar__item">
            Balance : <span>₹{walletBalance.toFixed(2)}</span>
          </Typography>
          <Typography className="mines-balance-bar__item mines-balance-bar__item--win">
            WIN : <span>₹{isEnded ? (session?.payout ?? 0) : isPlaying ? currentWin : 0}</span>
          </Typography>
        </Box>

        {isPlaying && session.revealedTiles.length > 0 && (
          <button
            type="button"
            className="mines-cashout-btn mines-cashout-btn--inline"
            onClick={cashOut}
            disabled={loading}
          >
            Cash out ₹{session.potentialWin}
            {session.nextMultiplier ? ` · next ${session.nextMultiplier}x` : ''}
          </button>
        )}

        <Box className="mines-controls" ref={pickersRef}>
          <Box className="mines-controls__pickers">
            <Box className="mines-controls__picker-wrap">
              <button
                type="button"
                className={`mines-controls__btn ${betPopupOpen ? 'active' : ''}`}
                disabled={!canChangeSettings}
                onClick={(e) => {
                  e.stopPropagation();
                  setMinesPopupOpen(false);
                  setBetPopupOpen((open) => !open);
                }}
              >
                Bet ₹{amount}
              </button>
              {betPopupOpen && canChangeSettings && (
                <MinesPickerPopup
                  options={betOptions}
                  value={stake}
                  prefix="₹"
                  onSelect={(v) => {
                    setAmount(String(v));
                    setBetPopupOpen(false);
                  }}
                />
              )}
            </Box>

            <Box className="mines-controls__picker-wrap">
              <button
                type="button"
                className={`mines-controls__btn ${minesPopupOpen ? 'active' : ''}`}
                disabled={!canChangeSettings}
                onClick={(e) => {
                  e.stopPropagation();
                  setBetPopupOpen(false);
                  setMinesPopupOpen((open) => !open);
                }}
              >
                Mines {mineCount}
              </button>
              {minesPopupOpen && canChangeSettings && (
                <MinesPickerPopup
                  options={allowedMineCounts}
                  value={mineCount}
                  onSelect={(v) => {
                    setMineCount(v);
                    setMinesPopupOpen(false);
                  }}
                />
              )}
            </Box>
          </Box>

          <button
            type="button"
            className="mines-reload-btn"
            onClick={reloadGame}
            disabled={!canReload}
            aria-label={isIdle || isEnded ? 'Start game' : 'Reload game'}
          >
            <ReplayRoundedIcon sx={{ fontSize: 28 }} />
          </button>
        </Box>

        {state?.recent?.length > 0 && !isPlaying && (
          <Box className="mines-history">
            <Typography className="mines-history__title">Recent games</Typography>
            {state.recent.map((g) => (
              <Box key={g.id} className="mines-history-row">
                <span>₹{g.amount} · {g.mineCount} mines</span>
                <span className={g.status === 'CASHED_OUT' ? 'win' : 'loss'}>
                  {g.status === 'CASHED_OUT' ? `+₹${g.payout}` : 'Busted'}
                </span>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MinesScreen;
